import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AxiosResponse } from "axios";
import { Box, InputLabel, Link, Typography } from "@mui/material";

import { useSnackBar } from "../../context/SnackBar";
import { useUserContext } from "../../context/UserContext";
import { api } from "../../api/base";
import {
  StyledAccountActionButton,
  StyledAccountContent,
  StyledAuthButton,
  StyledAuthInputFields,
  StyledDashContainer,
} from "../../components/micro/micro.styled";
import { Header } from "../../components/Header";

import { SubscriptionClass, SinglePlanItem } from "../../types/subscription";
import { CheckoutModal } from "../../components/CheckoutModal";
import { ChangeEmailModal } from "../../components/modals/ChangeEmail";
import { CancelPlanModal } from "../../components/modals/CancelPlan";
import { CancelPlanSuccessModal } from "../../components/modals/CancelPlanSuccess";
import { useDispatch, useSelector } from "react-redux";
import { paymentDetailsChanged } from "../../redux/actions/app";
import { PaymentDetailsChangedModal } from "../../components/modals/PaymentDetailsChangedModal";
import { ConfirmRenewalModal } from "../../components/modals/ConfirmRenewal";
import { ChangePasswordModal } from "../../components/modals/ChangePassword";
import { analyticsEvent } from "../../redux/actions/analytics";

import DBunny from "../../assets/dbunny.png";
import { UpgradeModal } from "./modal/UpgradeModal";
import { DawVersion } from "../../components/Daw/daw.version";
import {
  CampaignsInfo,
  tryEnterUserReferralCampaign,
  tryQueryCampaignsInfo,
} from "../../api/promoter";
// RTOBuyoutModal removed - component doesn't exist

import { isBFSale } from "../../utils/sales";
import {getDownloadLink, useDeviceInfo} from "../../utils/hooks/useDeviceInfo";

export const formatDateFromTimestamp = (timestamp: number): string => {
  const monthNames = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];

  const date = new Date(timestamp * 1000);

  const day = date.getDate();
  const monthIndex = date.getMonth();
  const year = date.getFullYear();

  return `${monthNames[monthIndex]} ${day}, ${year}`;
};

interface UserReferralText {
  headerPrefix: string;
  linkText: string;
  linkPath: string;
  description: string;

  refId: string;
  referralLink: string;

  activeReferrals: number;
  requiredReferrals: number;
  reward: string;
}

const tryExtractUserReferralText = async (
  campaignsInfo: CampaignsInfo,
): Promise<UserReferralText | null> => {
  let result: UserReferralText = {
    headerPrefix: "",
    linkText: "",
    linkPath: "",
    description: "",

    refId: campaignsInfo.user_referral.ref_id,
    referralLink: campaignsInfo.user_referral.referral_link,

    activeReferrals: Math.min(
      campaignsInfo.user_referral.active_referrals,
      campaignsInfo.user_referral.required_referrals,
    ),
    requiredReferrals: campaignsInfo.user_referral.required_referrals,
    reward: campaignsInfo.user_referral.reward,
  };

  switch (result.reward) {
    case "hardware":
      result.headerPrefix = "Get a Free ";
      result.linkText = "Rec-1";
      result.linkPath = "/hardware";
      result.description =
        "The Density recording hardware is perfect for clubs. " +
        `Refer ${result.requiredReferrals} friends, and we'll send you one for free.`;

      break;
    case "premium":
      result.headerPrefix = "Get a Free Year of ";
      result.linkText = "Density Premium";
      result.linkPath = "/pricing";
      result.description = `Refer ${result.requiredReferrals} friends, and we'll upgrade you for a year - free.`;

      break;
    case "pro":
      result.headerPrefix = "Get a Free License of ";
      result.linkText = "Density Pro v1";
      result.linkPath = "/pricing";
      result.description = `Refer ${result.requiredReferrals} friends, and we'll give you a license of Density Pro v1 - free.\nIf you have already purchased, we'll give you a full refund.`;

      break;
    default:
      console.warn("Unknown user referral reward:", result.reward);
      return null;
  }

  return result;
};

const tryQueryCampaignsAndExtractUserReferralText =
  async (): Promise<UserReferralText | null> => {
    const campaignsInfoOpt = await tryQueryCampaignsInfo();
    if (!campaignsInfoOpt) {
      return null;
    }

    return await tryExtractUserReferralText(campaignsInfoOpt);
  };

const tryEnterUserReferralCampaignAndExtractUserReferralText = async (
  reward: string,
): Promise<UserReferralText | null> => {
  const campaignsInfoOpt = await tryEnterUserReferralCampaign(reward);
  if (!campaignsInfoOpt) {
    return null;
  }

  return await tryExtractUserReferralText(campaignsInfoOpt);
};

export const ProfilePage = (): JSX.Element => {
  const dispatch = useDispatch();
  const { showSnackBar } = useSnackBar();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { setPlan, setUser, user, fetchingUserData, getProfile } = useUserContext();
  const [subscriptions, setSubscriptions] = useState<SubscriptionClass>(
    {} as SubscriptionClass,
  );
  const [plans, setPlans] = useState<SinglePlanItem[]>([]);
  const [renewalModalOpen, setRenewalModalOpen] = useState<boolean>(false);
  const [changeEmailModalOpen, setChangeEmailModalOpen] = useState<boolean>(
    searchParams.get("email") === "change" || false,
  );
  const [changePasswordModalOpen, setChangePasswordModalOpen] =
    useState<boolean>(false);
  const [cancelPlanModalOpen, setCancelPlanModalOpen] =
    useState<boolean>(false);
  const [cancelPlanSuccessModalOpen, setCancelPlanSuccessModalOpen] =
    useState<boolean>(false);
  const [loadingSubscription, setLoadingSubscription] = useState<boolean>(true);
  const blockingStatuses = ["payment_failed", "card_expired"];
  const [rtoBuyoutModalOpen, setRtoBuyoutModalOpen] = useState<boolean>(false);

  const [isLoadingReferralInfo, setIsLoadingReferralInfo] = useState(false);
  const [userReferralText, setUserReferralText] =
    useState<UserReferralText | null>(null);

  // Add upgradeData state for the styled checkout modal
  type upgradeDataType = {
    open: boolean;
    url: string;
    price?: string;
    additionalText?: string;
    title?: string;
    handleCloseModal?: () => void;
    image?: any;
  };
  const [upgradeData, setUpgradeData] = useState({} as upgradeDataType);

  const isPaymentModalOpen = useSelector(
    (state: any) => state.app.paymentDetailsChanged,
  );
  const deviceInfo = useDeviceInfo();

  const handleCloseModal = (): void => {
    setUpgradeData({ ...upgradeData, open: false, url: "", price: "" });
  };

  const handleClosePaymentDetailsModal = (): void => {
    dispatch(paymentDetailsChanged(false));
  };

  const getPlans = async (): Promise<void> => {
    try {
      const { data }: AxiosResponse<any, any> =
        await api.get("chargebee/plans");
      setPlans(data);
    } catch (err) {}
  };

  useEffect(() => {
    // Only redirect if we've finished fetching user data and user is not logged in
    // Check both the token and the user context state to avoid race conditions
    if (!fetchingUserData && !localStorage.getItem("access_token") && !user?.email) {
      navigate("/");
    }
  }, [fetchingUserData, navigate, user]);

  useEffect(() => {
    getPlans();
    if (searchParams.get("refresh_token") && searchParams.get("access_token")) {
      localStorage.setItem(
        "access_token",
        searchParams.get("access_token") || "",
      );
      localStorage.setItem(
        "refresh_token",
        searchParams.get("refresh_token") || "",
      );
      showSnackBar(
        "Successfully logged in!",
        "success",
        "center",
        2000,
        false,
        "auto",
        "95px",
      );
    }
    dispatch(
      analyticsEvent("PAGE_VISIT", "Account Management Visited", {
        page: "Account Management",
        email: user?.email,
      }),
    );
  }, []); // Run only once on mount

  useEffect(() => {
    if (
      (plans.length > 0 &&
        searchParams.get("fromDesktop") === "true" &&
        searchParams.get("upgrade") === "true") ||
      (plans.length > 0 && searchParams.get("upgrade") === "true") ||
      searchParams.get("reason") === "export"
    ) {
      buyPlan();
    }
    if (plans.length > 0 && searchParams.get("plan") === "hardware") {
      buyHardwarePlan();
    }
  }, [plans, searchParams]);

  // const getProfile = async (): Promise<void> => {
  //   try {
  //     const { data }: AxiosResponse<any, any> = await api.get("auth/profile");
  //     setUser(data.user);
  //     console.log('data.user', data.user);
  //     localStorage.setItem("user_email", data.user.email);
  //   } catch (err) { }
  // };

  const getSubscriptions = async (): Promise<void> => {
    try {
      const { data }: AxiosResponse<any, any> = await api.get(
        "chargebee/subscriptions",
      );
      setSubscriptions(data);
      setPlan({ planId: data.id, planName: data.item_price_id });
    } catch (err) {
      console.log("Failed to fetch subscription: ", err);
    } finally {
      setLoadingSubscription(false);
    }
  };

  const lowerMyRate = async () => {
    if (!subscriptions?.id) {
      console.error("No subscription ID available.");
      return;
    }

    try {
      // Check if this is an RTO subscription
      const isRtoSubscription = subscriptions?.item_price_id === 'rto-v1-15usd-14cycles-USD-Monthly';
      
      if (isRtoSubscription) {
        // Use the new RTO discount endpoint
        const response = await api.post("chargebee/apply-rto-discount");

        dispatch(
          analyticsEvent("CLICK", "Cancel-2.2: Clicked Lower My Rate (RTO)", {
            email: user.email,
          }),
        );
        dispatch(
          analyticsEvent("CLICK", "Rent to Own Buyout Initiated", {
            page: "Profile Page",
            plan_details: subscriptions?.item_price_id || "RTO Buyout",
          }),
        );

        if (response.data.details) {
          showSnackBar(
            `Monthly payment reduced to $${response.data.details.newMonthlyPrice}. Remaining payments: ${response.data.details.remainingMonths}`,
            "success",
            "center",
            3000,
            false,
            "auto",
            "95px",
          );
        } else {
          showSnackBar(
            response.data.message || "Successfully applied discount.",
            "success",
            "center",
            2000,
            false,
            "auto",
            "95px",
          );
        }
      } else {
        // Use the old lower subscription endpoint for non-RTO plans
        await api.post("chargebee/lower-subscription");

        dispatch(
          analyticsEvent("CLICK", "Cancel-2.2: Clicked Lower My Rate", {
            email: user.email,
          }),
        );

        showSnackBar(
          "Successfully changed subscription.",
          "success",
          "center",
          2000,
          false,
          "auto",
          "95px",
        );
      }

      // Add a small delay to ensure backend operations complete and webhooks are processed
      await new Promise(resolve => setTimeout(resolve, 1000));

      await getSubscriptions();
      // Refresh user profile to get updated RTO information
      await getProfile(true);
      setUpgradeData({ ...upgradeData, open: false });
    } catch (err: any) {
      console.error("Failed to lower subscription:", err);
      const errorMessage = err.response?.data?.message || "An error occurred during the subscription change.";
      showSnackBar(
        errorMessage,
        "error",
      );
    }
  };

  const handleKeepSubscription = async () => {
    const isRtoSubscription = subscriptions?.item_price_id === 'rto-v1-15usd-14cycles-USD-Monthly' ||
                              subscriptions?.item_price_id?.toLowerCase().includes('rto');
    
    // Use resume for paused RTO, renew for others
    if (isRtoSubscription && subscriptions?.status === 'paused') {
      resumeRTOSubscription();
      dispatch(
        analyticsEvent("CLICK", "Cancel-4.2: Resume RTO", {
          page: "Cancel Modal",
          email: user?.email,
        }),
      );
      dispatch(
        analyticsEvent("CLICK", "Rent to Own Plan Resumed", {
          page: "Profile Page",
          plan_details: subscriptions?.item_price_id || "RTO Plan",
        }),
      );
    } else {
      renewSubscription();
      dispatch(
        analyticsEvent("CLICK", "Cancel-4.2: Keep Subscription", {
          page: "Cancel Modal",
          email: user?.email,
        }),
      );
    }
    setCancelPlanSuccessModalOpen(false);
  };

  const handleCancelScheduledPauseFromModal = async () => {
    try {
      const { data }: AxiosResponse<any, any> = await api.post(
        "chargebee/cancel-scheduled-pause",
        {
          itemId: subscriptions.id,
        },
      );
      
      showSnackBar(
        "Successfully canceled the scheduled pause.",
        "success",
        "center",
        2000,
        false,
        "auto",
        "95px",
      );
      
      // Refresh subscriptions and user profile to update the UI
      await Promise.all([
        getSubscriptions(),
        getProfile(true)
      ]);
      
      dispatch(
        analyticsEvent("CLICK", "Cancel-4.2: Cancel Scheduled Pause", {
          page: "Cancel Modal",
          email: user?.email,
        }),
      );
      dispatch(
        analyticsEvent("CLICK", "Rent to Own Plan Paused", {
          page: "Cancel Modal",
          plan_details: subscriptions?.item_price_id || "RTO Pause",
        }),
      );
      dispatch(
        analyticsEvent("CLICK", "Rent to Own Plan Paused", {
          page: "Cancel Modal",
          plan_details: "TODO: Populate RTO Plan Details",
        }),
      );
      
      setCancelPlanSuccessModalOpen(false);
    } catch (err: any) {
      const { response } = err;
      console.error('Cancel scheduled pause error:', err);
      showSnackBar(
        response?.data?.error_msg || response?.data?.message || "Failed to cancel scheduled pause",
        "error"
      );
    }
  };

  const handleCancelPause = async () => {
    const isRtoSubscription = subscriptions?.item_price_id === 'rto-v1-15usd-14cycles-USD-Monthly' ||
                              subscriptions?.item_price_id?.toLowerCase().includes('rto');
    
    if (isRtoSubscription && subscriptions?.pause_date) {
      try {
        const { data }: AxiosResponse<any, any> = await api.post(
          "chargebee/cancel-scheduled-pause",
          {
            itemId: subscriptions.id,
          },
        );
        
        showSnackBar(
          "Successfully canceled the scheduled pause.",
          "success",
          "center",
          2000,
          false,
          "auto",
          "95px",
        );
        
        // Refresh subscriptions and user profile to update the UI
        await Promise.all([
          getSubscriptions(),
          getProfile(true)
        ]);
        
        dispatch(
          analyticsEvent("CLICK", "Cancel Scheduled Pause", {
            page: "Profile",
            email: user?.email,
          }),
        );
        dispatch(
          analyticsEvent("CLICK", "Rent to Own Plan Paused", {
            page: "Profile",
            plan_details: subscriptions?.item_price_id || "RTO Pause",
          }),
        );
        dispatch(
          analyticsEvent("CLICK", "Rent to Own Plan Paused", {
            page: "Profile",
            plan_details: "TODO: Populate RTO Plan Details",
          }),
        );
      } catch (err: any) {
        const { response } = err;
        console.error('Cancel scheduled pause error:', err);
        showSnackBar(
          response?.data?.error_msg || response?.data?.message || "Failed to cancel scheduled pause",
          "error"
        );
      }
    }
  };

  const renewSubscription = async (): Promise<void> => {
    setRenewalModalOpen(false);
    try {
      const { data }: AxiosResponse<any, any> = await api.post(
        "chargebee/renew-subscription",
        {
          planId: subscriptions.id,
          trialEnd: subscriptions.trial_end,
          status: subscriptions.status,
        },
      );
      showSnackBar(
        "Successfully reactivated subscription.",
        "success",
        "center",
        2000,
        false,
        "auto",
        "95px",
      );
      // Refresh both subscription and user profile data to update RTO information
      await Promise.all([
        getSubscriptions(),
        getProfile() // This will refresh the user data including RTO details
      ]);
    } catch (err: any) {
      const { response } = err;
      showSnackBar(response.data.error_msg, "error");
    }
  };

  const resumeRTOSubscription = async (): Promise<void> => {
    try {
      // For paused RTO, we need to use the RTO subscription ID from user data, not the current subscription ID
      const rtoSubscriptionId = user?.rto?.subscriptionId || subscriptions.id;
      
      if (!rtoSubscriptionId) {
        showSnackBar("Unable to find your RTO subscription. Please contact support.", "error");
        return;
      }
      
      const { data }: AxiosResponse<any, any> = await api.post(
        "chargebee/resume-rto",
        {
          itemId: rtoSubscriptionId,
        },
      );
      // Add a small delay to ensure backend operations complete
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Redirect to upgrade-success page
      navigate("/account/upgrade-success?plan=rto");
    } catch (err: any) {
      const { response } = err;
      console.error('Resume RTO error:', err);
      showSnackBar(response?.data?.error_msg || response?.data?.message || "Failed to resume subscription", "error");
    }
  };

  const handleSubscriptionRenewal = async (): Promise<void> => {
    renewSubscription();
  };

  const buyPlan = async (): Promise<void> => {
    // Wait for user data to be loaded
    if (fetchingUserData) {
      console.log("Waiting for user data to load...");
      showSnackBar("Loading user data, please wait...", "info");
      return;
    }

    try {
      const plan =
        plans.find((plan: any) => plan.id === "Premium-USD-Monthly") ||
        plans[0];
      const { data }: AxiosResponse<any, any> = await api.post(
        "chargebee/checkout",
        {
          item_price_id: plan.id,
          unit_price: plan.price,
          redirect_url: `${process.env.REACT_APP_URL}/account${searchParams.get("fromDesktop") ? "?fromDesktop=true" : ""}`,
          coupon: isBFSale() ? "BF2025" : "",
        },
      );
      
      const handleCloseModal = () => {
        setUpgradeData({ ...upgradeData, open: false });
      };
      
      setUpgradeData({
        ...upgradeData,
        url: data.url,
        price: isBFSale() ? "BF2025" : "$15/month",
        title: "Premium Plan",
        open: true,
        handleCloseModal,
      });
    } catch (err) {
      console.error("Failed to initiate checkout:", err);
      showSnackBar("Failed to initiate checkout. Please try again.", "error");
    }
  };

  const buyPro = async (): Promise<void> => {
    // Wait for user data to be loaded
    if (fetchingUserData) {
      console.log("Waiting for user data to load...");
      showSnackBar("Loading user data, please wait...", "info");
      return;
    }

    try {
      console.log("buyPro called - initiating Pro checkout");
      const payload = {
        item_price_id: "density-pro-v1-charge-USD",
        redirect_url: `${process.env.REACT_APP_URL || window.location.origin}/account/upgrade-success?plan=density-pro-v1${searchParams.get("fromDesktop") ? "&fromDesktop=true" : ""}`,
        ...(isBFSale() && { coupon: "BF2025" }),
      };
      console.log("Sending payload:", payload);

      const { data }: AxiosResponse<any, any> = await api.post(
        "chargebee/checkout",
        payload,
      );
      console.log("Received checkout URL:", data.url);
      
      if (data.url) {
        const handleCloseModal = () => {
          setUpgradeData({ ...upgradeData, open: false });
        };
        
        setUpgradeData({
          ...upgradeData,
          url: data.url,
          price: "Version 1",
          title: "Density Pro",
          open: true,
          handleCloseModal,
        });
      }
    } catch (err: any) {
      console.error("Failed to initiate Density Pro checkout:", err);
      // Check if the error is due to already having a subscription
      if (err.response?.status === 400 && err.response?.data?.message) {
        showSnackBar(err.response.data.message, "error");
      } else {
        showSnackBar("Failed to initiate checkout. Please try again.", "error");
      }
    }
  };

  const calculateMonthsRemaining = (): number => {
    if (!user?.rto_installments_paid || !user?.rto_total_installments) {
      return 14; // Default to full term if no data
    }
    return user.rto_total_installments - user.rto_installments_paid;
  };

  const handleRTOBuyout = async (): Promise<void> => {
    try {
      dispatch(
        analyticsEvent("CLICK", "Rent to Own Buyout Initiated", {
          page: "Account Management",
          plan_details: subscriptions?.item_price_id || "RTO Buyout",
        }),
      );
      const payload = {
        redirect_url: `${process.env.REACT_APP_URL || window.location.origin}/account/upgrade-success?plan=rto-buyout`,
        cancel_url: `${process.env.REACT_APP_URL || window.location.origin}/account`
      };
      
      const { data }: AxiosResponse<any, any> = await api.post(
        "chargebee/rto-buyout",
        payload
      );
      
      if (data.url) {
        const handleCloseModal = () => {
          setUpgradeData({ ...upgradeData, open: false });
        };
        
        setUpgradeData({
          ...upgradeData,
          url: data.url,
          price: "",
          title: "Density Pro",
          open: true,
          handleCloseModal,
        });
      }
    } catch (err) {
      console.error("Failed to initiate RTO buyout:", err);
      showSnackBar("Failed to initiate buyout. Please try again.", "error");
    }
  };

  const buyRentToOwn = async (): Promise<void> => {
    // Wait for user data to be loaded
    if (fetchingUserData) {
      console.log("Waiting for user data to load...");
      showSnackBar("Loading user data, please wait...", "info");
      return;
    }

    // Check if user has a paused RTO subscription
    const hasPausedRTO = user?.rto && user?.rto?.status === 'paused';
    
    if (hasPausedRTO) {
      // If RTO is paused, we need to find the paused RTO subscription ID
      // The user.rto object should contain the subscription ID
      console.log("User has paused RTO, checking for subscription ID");
      
      if (user.rto?.subscriptionId) {
        // Use the dedicated resume function which has proper refresh logic
        await resumeRTOSubscription();
        return;
      } else {
        showSnackBar("Unable to find your paused subscription. Please contact support.", "error");
        return;
      }
    }

    try {
      console.log("buyRentToOwn called - initiating Rent to Own checkout");
      
      const payload = {
        item_price_id: isBFSale() ? "rto-v1-bf2025-USD-Monthly" : "rto-v1-15usd-14cycles-USD-Monthly",
        redirect_url: `${process.env.REACT_APP_URL || window.location.origin}/account/upgrade-success?plan=rto${searchParams.get("fromDesktop") ? "&fromDesktop=true" : ""}`,
      };
      console.log("Sending payload:", payload);
      
      const { data }: AxiosResponse<any, any> = await api.post(
        "chargebee/checkout",
        payload,
      );
      console.log("Received checkout URL:", data.url);
      
      const handleCloseModal = () => {
        setUpgradeData({ ...upgradeData, open: false });
      };
      
      setUpgradeData({
        ...upgradeData,
        url: data.url,
        price: isBFSale() ? "$7.86/month for 14 months" : "$15/month for 14 months",
        title: "Density Pro - Rent to Own",
        open: true,
        handleCloseModal,
      });
    } catch (err) {
      console.error("Failed to initiate Rent to Own checkout:", err);
      showSnackBar("Failed to initiate checkout. Please try again.", "error");
    }
  };

  const buyHardwarePlan = async (): Promise<void> => {
    try {
      const { data }: AxiosResponse<any, any> = await api.post(
        "chargebee/checkout-hardware",
        {
          item_price_id: "Premium-USD-Yearly",
          unit_price: 1500,
          redirect_url: `${process.env.REACT_APP_URL}/account`,
        },
      );
      
      const handleCloseModal = () => {
        setUpgradeData({ ...upgradeData, open: false });
      };
      
      setUpgradeData({
        ...upgradeData,
        url: data.url,
        price: "Subscription - $180/year",
        additionalText: "Hardware - $300 one time",
        title: "Hardware Plan",
        open: true,
        handleCloseModal,
      });
    } catch (err) {
      console.error("Failed to initiate hardware checkout:", err);
      showSnackBar("Failed to initiate checkout. Please try again.", "error");
    }
  };

  // Removed useEffect for url - now managed through upgradeData state

  useEffect(() => {
    if (searchParams.get("state") === "succeeded") {
      navigate(
        `/account/upgrade-success${searchParams.get("fromDesktop") ? "?fromDesktop=true" : ""}`,
      );
      dispatch(
        analyticsEvent("PAYMENT", "Payment Completed", {
          page: "Account Management",
          plan: "premium",
        }),
      );
    }

    if (searchParams.get("state") === "failed") {
      const failedPlan = searchParams.get("plan");
      if (failedPlan === "rto" || failedPlan === "rto-buyout") {
        dispatch(
          analyticsEvent("PAYMENT", "Rent to Own Payment Failed", {
            page: "Account Management",
            plan: failedPlan,
            error_details: "TODO: Populate error details",
          }),
        );
      } else {
        dispatch(
          analyticsEvent("PAYMENT", "Payment failed", {
            page: "Account Management",
            plan: failedPlan || "unknown",
          }),
        );
      }
    }
  }, []);

  useEffect(() => {
    getSubscriptions();
  }, []);

  const handleRenewal = async (): Promise<void> => {
    if (typeof subscriptions?.next_billing_at === "undefined") {
      const type =
        typeof subscriptions?.next_billing_at === "undefined"
          ? subscriptions?.status === "non_renewing"
            ? `Reactivate`
            : `Renew`
          : "";
      dispatch(
        analyticsEvent("CLICK", `${type} Pressed`, {
          page: "Account Management",
          email: user?.email,
        }),
      );
      handleRenewalConfirmation();
    } else {
      dispatch(
        analyticsEvent("CLICK", "Cancel-1: Clicked Cancel", {
          page: "Account Management",
          email: user?.email,
        }),
      );
      setCancelPlanModalOpen(true);
    }
  };

  const hasTrialExpired = () => {
    const trialEnd = new Date(subscriptions?.trial_end * 1000);
    const today = new Date();
    if (!subscriptions?.trial_end) return true;
    return today > trialEnd;
  };

  const handleRenewalConfirmation = () => {
    setRenewalModalOpen(true);
  };

  const checkUserStatus = (subscription: any, status: string): string => {
    if (blockingStatuses.includes(status)) {
      return status.replace("_", " ");
    }
    
    // Check if it's a pro license (any pro plan)
    if (subscription?.item_price_id?.includes("density-pro")) {
      // Extract version from item_price_id (e.g., "density-pro-plan-v1-USD-Monthly" -> "v1")
      const versionMatch = subscription.item_price_id.match(/density-pro-(?:plan-)?(v\d+)/);
      const version = versionMatch ? versionMatch[1] : "v1";
      return `Density Pro ${version}`;
    }
    
    // Check if it's RTO plan
    if (subscription?.item_price_id?.includes("rto-v1")) {
      return "Density Pro v1 (Rent-to-Own)";
    }
    
    const planNames: any = {
      "Medium-USD-Monthly": "Premium",  // Changed from "Artist" to "Premium" to match backend
      "Premium-USD-Monthly": "Premium",
      "Premium-USD-Yearly": "Premium",
      "Pro-USD-Monthly": "Premium",  // Backend maps this to premium
      "PremiumHardware-USD-Yearly": "Premium Hardware",
      // New plan ID formats
      "density-premium-plan-v1-USD-Monthly": "Premium",
      "density-premium-plan-v1-USD-Yearly": "Premium",
      // Add Free plan
      "Free-USD-Monthly": "Free",
    };
    const durationMatrix: any = {
      "Medium-USD-Monthly": "Monthly",
      "Premium-USD-Monthly": "Monthly",
      "Premium-USD-Yearly": "Yearly",
      "Pro-USD-Monthly": "Monthly",
      "PremiumHardware-USD-Yearly": "Yearly",
      // New plan ID formats
      "density-premium-plan-v1-USD-Monthly": "Monthly",
      "density-premium-plan-v1-USD-Yearly": "Yearly",
      "Free-USD-Monthly": "Monthly",
    };
    // If the item_price_id isn't in planNames, default to "Free"
    const planName = planNames[subscription?.item_price_id] || "Free";
    
    // For free plans, don't show price information
    if (planName === "Free" || !subscription?.unit_price) {
      return `${subscription?.status?.replace("_", " ") || ""} (Free Plan)`.trim();
    }
    
    // For paid plans, show full information
    return `${subscription?.status?.replace("_", " ")} (${planName} Plan, $${subscription.unit_price / 100}/${durationMatrix[subscription?.item_price_id] || "Monthly"})`;
  };

  const handleCancelPlanSuccessModalClose = () => {
    dispatch(
      analyticsEvent("CLICK", "Cancel-4.1: Cancel pressed", {
        page: "Cancel Modal",
        email: user?.email,
      }),
    );
    setCancelPlanSuccessModalOpen(false);
  };

  const handleDownload = () => {
    dispatch(
      analyticsEvent("EVENT", "Download Density App", {
        "OS Downloaded": "Universal",
        location: "Account Management Page",
      }),
    );

    api
      .post("/auth/download", {
        email: user?.email,
        os: "universal",
      })
      .then((res) => {
        // console.log(res);
      })
      .catch((err) => {
        // console.log(err);
      });
  };

  const fetchReferralInfo = async () => {
    setIsLoadingReferralInfo(true);
    try {
      const newUserReferralTextOpt =
        await tryQueryCampaignsAndExtractUserReferralText();
      
      if (newUserReferralTextOpt) {
        setUserReferralText(newUserReferralTextOpt);
      } else {
        // Set default referral text for non-enrolled users
        const defaultReferral: UserReferralText = {
          refId: '',
          referralLink: '',
          activeReferrals: 0,
          requiredReferrals: 4,
          reward: 'pro',
          headerPrefix: 'Get a Free License of ',
          linkText: 'Density Pro v1',
          linkPath: '/pricing',
          description: 'Refer 4 friends, and we\'ll give you a license of Density Pro v1 - free.\nIf you have already purchased, we\'ll give you a full refund.',
        };
        setUserReferralText(defaultReferral);
      }
    } finally {
      setIsLoadingReferralInfo(false);
    }
  };

  useEffect(() => {
    fetchReferralInfo();
  }, []);

  const handleCopyUserReferralLink = () => {
    if (!userReferralText || !userReferralText.referralLink) {
      return;
    }

    dispatch(
      analyticsEvent("EVENT", "Referral Link Copied", {
        location: "Account Management",
        refId: userReferralText.refId || "",
        reward: userReferralText.reward || "",
      }),
    );

    navigator.clipboard.writeText(userReferralText.referralLink);
    showSnackBar("Link copied to clipboard", "success");
  };

  const handleGenerateUserReferralLink = async () => {
    const reward = userReferralText?.reward;
    if (!reward) {
      return;
    }

    setIsLoadingReferralInfo(true);
    try {
      const newUserReferralTextOpt =
        await tryEnterUserReferralCampaignAndExtractUserReferralText(reward);
      if (newUserReferralTextOpt) {
        dispatch(
          analyticsEvent("EVENT", "Referral Link Created", {
            location: "Account Management",
            refId: newUserReferralTextOpt.refId || "",
            reward: newUserReferralTextOpt.reward || "",
          }),
        );

        setUserReferralText(newUserReferralTextOpt);
      } else {
        showSnackBar("An error occurred while generating the link", "error");
      }
    } finally {
      setIsLoadingReferralInfo(false);
    }
  };

  return (
    <StyledDashContainer sx={{height:"auto"}}>
      <Header />
      <Box
        gap={"24px"}
        justifyContent={"center"}
        alignItems={"center"}
        flexDirection={"column"}
        display={"flex"}
        width={"100%"}
        borderTop={"1px solid #0d0c10"}
        padding={"0 10px"}
        paddingBottom={"20px"}
      >
        <Typography
          mt={"62px"}
          variant="h2"
          color="rgba(252, 252, 252, 0.6)"
          alignSelf={"left"}
          marginBottom={"8px"}
          maxWidth={"clamp(550px, 45vw, 700px)"}
          width={"100%"}
        >
          Manage account
        </Typography>
        <StyledAccountContent>
          <Typography
            display={"flex"}
            justifyContent={"space-between"}
            variant="h2"
            color="#F3F3F5"
          >
            Account
          </Typography>
          <Box display={"flex"} alignItems={"flex-start"}>
            <Typography
              display={"flex"}
              variant="body1"
              fontSize={12}
              alignItems={"center"}
            >
              Email:&nbsp;
              <span style={{ color: "#fcfcfc" }}>{user?.email}</span>
            </Typography>
            <Box
              marginLeft={"auto"}
              display={"flex"}
              flexDirection={"column"}
              alignItems={"flex-end"}
            >
              <StyledAccountActionButton
                onClick={() => setChangeEmailModalOpen(true)}
                variant={"text"}
                sx={{ color: "#575F9E" }}
              >
                Change e-mail
              </StyledAccountActionButton>
              <StyledAccountActionButton
                onClick={() => setChangePasswordModalOpen(true)}
                variant={"text"}
                sx={{ color: "#575F9E" }}
              >
                Change password
              </StyledAccountActionButton>
            </Box>
          </Box>
        </StyledAccountContent>
        <StyledAccountContent>
          <Typography
            display={"flex"}
            justifyContent={"space-between"}
            variant="h2"
            color="#F3F3F5"
          >
            Licence
          </Typography>
          {Object.keys(subscriptions).length === 0 ||
          subscriptions?.item_price_id === "Free-USD-Monthly" ||
          (user?.plan === "free" && user?.rto?.status !== 'paused') ||
          (subscriptions?.item_price_id && subscriptions.item_price_id.toLowerCase().includes("free")) ||
          (['cancelled'].includes(subscriptions?.status) && ( // Changed from ['cancelled', 'non_renewing']
            subscriptions?.item_price_id === "Premium-USD-Monthly" ||
            subscriptions?.item_price_id === "Medium-USD-Monthly" ||
            subscriptions?.item_price_id === "Premium-USD-Yearly" ||
            subscriptions?.item_price_id === "Pro-USD-Monthly" ||
            subscriptions?.item_price_id === "PremiumHardware-USD-Yearly" ||
            subscriptions?.item_price_id === "density-premium-plan-v1-USD-Monthly" ||
            subscriptions?.item_price_id === "density-premium-plan-v1-USD-Yearly"
          )) ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "16px",
              }}
            >
              <Typography
                display={"flex"}
                justifyContent={"space-between"}
                variant="body1"
                fontSize={12}
                alignItems={"center"}
              >
                Status: {loadingSubscription ? "Loading..." : "Free plan"}
                {/* <StyledAccountActionButton
                  onClick={buyPlan}
                  variant={"text"}
                  sx={{ color: "#575F9E" }}
                >
                  Upgrade
                </StyledAccountActionButton> */}
              </Typography>

              <div
                style={{
                  display: loadingSubscription ? "none" : "flex",
                  background:
                    "linear-gradient(264.82deg, #686FEF -4.46%, rgba(104, 111, 239, 0.25) 51.84%, rgba(104, 111, 239, 0) 108.77%)",
                }}
                className="flex w-full items-center justify-between rounded px-[32px] py-[24px]"
              >
                <div className="flex items-center">
                  <img
                    src={DBunny}
                    className="mr-[16px] h-[53px] w-[45px] object-contain"
                    alt=""
                  />
                  <div>
                    <p className="mb-[8px] font-PPRightGrotesk-SpatialBlack text-[18px] leading-[24px] tracking-[0.15px] text-[#E6E8F1]">
                      Density Pro v1
                    </p>
                    <p className="text-[12px] font-medium leading-[18px] tracking-[0.1px] text-[#C1C5DD]">
                      Upgrade to remove the watermark
                      {isBFSale() && (
                        <p className="!text-alert-orange-800 mt-[8px] text-[14px]">
                          Fall Sale Sale - up to $100 off
                        </p>
                      )}
                    </p>
                  </div>
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                  <StyledAuthButton
                    onClick={buyPro}
                    variant={"contained"}
                    sx={{
                      m: 0,
                      width: user?.rto && user?.rto?.status === 'paused' ? 'auto' : 110,
                      background: "#686FEF",
                      px: user?.rto && user?.rto?.status === 'paused' ? "16px" : "28px",
                      py: "10px",
                      fontWeight: 600,
                      color: "#FFFFFF",
                      "&:hover": {
                        background: "#5A61D6",
                      }
                    }}
                  >
                    Purchase
                  </StyledAuthButton>
                  <StyledAuthButton
                    onClick={buyRentToOwn}
                    variant={"contained"}
                    sx={{
                      m: 0,
                      width: 110,
                      background: "#686FEF",
                      px: "28px",
                      py: "10px",
                      fontWeight: 600,
                      color: "#FFFFFF",
                      "&:hover": {
                        background: "#5A61D6",
                      }
                    }}
                  >
                    {user?.rto && user?.rto?.status === 'paused' ? (
                      <>
                        Rent-to-Own<br />(Resume)
                      </>
                    ) : 'Rent-to-Own'}
                  </StyledAuthButton>
                </div>
              </div>
            </Box>
          ) : (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "16px",
              }}
            >
              {/* For RTO users, don't show the Version line */}
              {!subscriptions?.item_price_id?.includes("rto-v1") && (
                <Typography
                  alignItems={"center"}
                  display={"flex"}
                  justifyContent={""}
                  variant="body1"
                  fontSize={12}
                >
                  {subscriptions?.item_price_id?.includes("density-pro") ? (
                    <>
                      Version:&nbsp;
                      <span style={{ color: "#fcfcfc" }}>
                        {loadingSubscription
                          ? "Loading..."
                          : checkUserStatus(subscriptions, user?.status)}
                      </span>
                    </>
                  ) : (
                  <>
                    Status:&nbsp;
                    <span style={{ color: "#fcfcfc", textTransform: "capitalize" }}>
                      {loadingSubscription
                        ? "Loading..."
                        : checkUserStatus(subscriptions, user?.status)}
                    </span>
                    <StyledAccountActionButton
                      onClick={() => handleRenewal()}
                      variant={"text"}
                      sx={{ color: "#575F9E", marginLeft: "auto" }}
                    >
                      {typeof subscriptions?.next_billing_at === "undefined"
                        ? subscriptions?.status === "non_renewing"
                          ? `Reactivate`
                          : `Renew for $${subscriptions?.unit_price / 100}`
                        : "Cancel"}
                    </StyledAccountActionButton>
                  </>
                  )}
                </Typography>
              )}
              
              {/* RTO Status Display */}
              {user?.rto && subscriptions?.item_price_id?.includes("rto-v1") && (
                <>
                  {/* For cancelled/scheduled pause RTO subscriptions, show standard format */}
                  {subscriptions?.status === 'non_renewing' ? (
                    <>
                      <Typography
                        alignItems={"center"}
                        display={"flex"}
                        justifyContent={""}
                        variant="body1"
                        fontSize={12}
                      >
                        Status:&nbsp;
                        <span style={{ color: "#fcfcfc", textTransform: "capitalize" }}>
                          Non Renewing (Rent-to-Own, ${subscriptions?.unit_price ? (subscriptions.unit_price / 100).toFixed(2) : user.rto?.monthlyAmount || '0'}/Monthly)
                        </span>
                        <StyledAccountActionButton
                          onClick={() => handleRenewal()}
                          variant={"text"}
                          sx={{ color: "#575F9E", marginLeft: "auto" }}
                        >
                          REACTIVATE
                        </StyledAccountActionButton>
                      </Typography>
                      
                      <Typography
                        alignItems={"center"}
                        display={"flex"}
                        variant="body1"
                        fontSize={12}
                      >
                        Active until:&nbsp;
                        <span style={{ color: "#fcfcfc" }}>
                          {subscriptions?.current_term_end
                            ? formatDateFromTimestamp(subscriptions.current_term_end)
                            : new Date(user.rto.nextPaymentDate).toLocaleDateString()}
                        </span>
                      </Typography>
                    </>
                  ) : subscriptions?.status === 'paused' ? (
                    <>
                      <Typography
                        alignItems={"center"}
                        display={"flex"}
                        justifyContent={""}
                        variant="body1"
                        fontSize={12}
                      >
                        Status:&nbsp;
                        <span style={{ color: "#fcfcfc", textTransform: "capitalize" }}>
                          Paused (Rent-to-Own, ${subscriptions?.unit_price ? (subscriptions.unit_price / 100).toFixed(2) : user.rto?.monthlyAmount || '0'}/Monthly)
                        </span>
                        <StyledAccountActionButton
                          onClick={() => resumeRTOSubscription()}
                          variant={"text"}
                          sx={{ color: "#575F9E", marginLeft: "auto" }}
                        >
                          RESUME
                        </StyledAccountActionButton>
                      </Typography>
                      
                      {subscriptions?.pause_date && (
                        <Typography
                          alignItems={"center"}
                          display={"flex"}
                          variant="body1"
                          fontSize={12}
                        >
                          Paused since:&nbsp;
                          <span style={{ color: "#fcfcfc" }}>
                            {formatDateFromTimestamp(subscriptions.pause_date)}
                          </span>
                        </Typography>
                      )}
                    </>
                  ) : (
                    /* For active RTO subscriptions, show detailed RTO info */
                    <Box sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "12px",
                    }}>
                      <Box display={"flex"} alignItems={"flex-start"}>
                        <Box sx={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                          <Typography
                            display={"flex"}
                            variant="body1"
                            fontSize={12}
                          >
                            Status:&nbsp;
                            <span style={{ color: "#fcfcfc", textTransform: "capitalize" }}>
                              Active, Rent-to-Own{subscriptions?.pause_date ? " (Scheduled for pause)" : ""}
                            </span>
                          </Typography>
                          
                          <Typography
                            display={"flex"}
                            variant="body1"
                            fontSize={12}
                          >
                            Start Date:&nbsp;
                            <span style={{ color: "#fcfcfc" }}>
                              {new Date(user.rto.startDate).toLocaleDateString()}
                            </span>
                          </Typography>
                          
                          <Typography
                            display={"flex"}
                            variant="body1"
                            fontSize={12}
                          >
                            Months Remaining:&nbsp;
                            <span style={{ color: "#fcfcfc" }}>
                              {user.rto.monthsRemaining} @ ${user.rto.monthlyAmount}/month
                            </span>
                          </Typography>
                          
                          {/* Only show next payment if no pause is scheduled */}
                          {!subscriptions?.pause_date ? (
                            <Typography
                              display={"flex"}
                              variant="body1"
                              fontSize={12}
                            >
                              Next Payment:&nbsp;
                              <span style={{ color: "#fcfcfc" }}>
                                {new Date(user.rto.nextPaymentDate).toLocaleDateString()}
                              </span>
                            </Typography>
                          ) : (
                            <Typography
                              display={"flex"}
                              variant="body1"
                              fontSize={12}
                            >
                              Scheduled to pause on:&nbsp;
                              <span style={{ color: "#fcfcfc" }}>
                                {formatDateFromTimestamp(subscriptions.pause_date)}
                              </span>
                            </Typography>
                          )}
                        </Box>
                        
                        <Box
                          marginLeft={"auto"}
                          display={"flex"}
                          flexDirection={"column"}
                          alignItems={"flex-end"}
                        >
                          <StyledAccountActionButton
                            onClick={handleRTOBuyout}
                            variant={"text"}
                            sx={{ color: "#575F9E" }}
                          >
                            BUY-NOW
                          </StyledAccountActionButton>
                          
                          {subscriptions?.pause_date ? (
                            <StyledAccountActionButton
                              onClick={handleCancelPause}
                              variant={"text"}
                              sx={{ color: "#575F9E" }}
                            >
                              CANCEL PAUSE
                            </StyledAccountActionButton>
                          ) : (
                            <StyledAccountActionButton
                              onClick={() => handleRenewal()}
                              variant={"text"}
                              sx={{ color: "#575F9E" }}
                            >
                              CANCEL
                            </StyledAccountActionButton>
                          )}
                        </Box>
                      </Box>
                    </Box>
                  )}
                </>
              )}
              
              {/* Show owned status for completed RTO */}
              {user?.plan === "pro-v1" && user?.license_type === "rto_owned" && (
                <Typography
                  alignItems={"center"}
                  display={"flex"}
                  variant="body1"
                  fontSize={12}
                  sx={{ mt: 2, color: "#4caf50" }}
                >
                  ✓ Fully owned through Rent-to-Own program
                </Typography>
              )}
              
              {!subscriptions?.item_price_id?.includes("density-pro") && !subscriptions?.item_price_id?.includes("rto-v1") && (
                <>
                  {!hasTrialExpired() && (
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "16px",
                      }}
                    >
                      <Typography
                        alignItems={"center"}
                        display={"flex"}
                        variant="body1"
                        fontSize={12}
                      >
                        Start date:&nbsp;
                        <span
                          style={{ color: "#fcfcfc", textTransform: "capitalize" }}
                        >
                          {formatDateFromTimestamp(subscriptions?.trial_start)}
                        </span>
                      </Typography>
                      <Typography display={"flex"} variant="body1" fontSize={12}>
                        Trial end date:&nbsp;
                        <span
                          style={{ color: "#fcfcfc", textTransform: "capitalize" }}
                        >
                          {formatDateFromTimestamp(subscriptions?.trial_end)}
                        </span>
                      </Typography>{" "}
                    </Box>
                  )}
                  {!blockingStatuses.includes(user?.status) &&
                  subscriptions?.next_billing_at &&
                  !subscriptions?.pause_date ? (
                    <Typography
                      alignItems={"center"}
                      display={"flex"}
                      variant="body1"
                      fontSize={12}
                    >
                      Next payment date:&nbsp;
                      <span
                        style={{ color: "#fcfcfc", textTransform: "capitalize" }}
                      >
                        {formatDateFromTimestamp(subscriptions?.next_billing_at)}
                      </span>
                    </Typography>
                  ) : subscriptions?.status === "non_renewing" ? (
                    <Typography
                      alignItems={"center"}
                      display={"flex"}
                      variant="body1"
                      fontSize={12}
                    >
                      Active until:&nbsp;
                      <span
                        style={{ color: "#fcfcfc", textTransform: "capitalize" }}
                      >
                        {formatDateFromTimestamp(subscriptions?.current_term_end)}
                      </span>
                    </Typography>
                  ) : (
                    <Typography
                      alignItems={"center"}
                      display={"flex"}
                      variant="body1"
                      fontSize={12}
                    >
                      Subscription cancelled
                    </Typography>
                  )}
                </>
              )}
            </Box>
          )}
        </StyledAccountContent>
        <StyledAccountContent>
          <Typography
            alignItems={"center"}
            display={"flex"}
            justifyContent={"space-between"}
            variant="h2"
            color="#F3F3F5"
          >
            Payment method
            <Typography variant={"body1"} fontSize={12} ml={"auto"}>
              {user?.card
                ?.slice(8)
                .match(/.{1,4}/g)
                ?.join(" ")}
            </Typography>
            <StyledAccountActionButton
              onClick={() => navigate("payment-method")}
              variant={"text"}
              sx={{ color: "#575F9E", ml: 3 }}
            >
              Edit
            </StyledAccountActionButton>
          </Typography>
        </StyledAccountContent>

        {userReferralText !== null && (
          <StyledAccountContent>
            <Box
              display={"flex"}
              alignItems={"center"}
              justifyContent={"space-between"}
            >
              <Typography variant={"h2"}>
                {userReferralText.headerPrefix}
                <span>
                  <Link href={userReferralText.linkPath}>
                    {userReferralText.linkText}
                  </Link>
                </span>
              </Typography>
            </Box>
            <Typography
              alignItems={"center"}
              display={"flex"}
              justifyContent={""}
              variant="body1"
              fontSize={12}
              sx={{ whiteSpace: 'pre-line' }}
            >
              {userReferralText.description}
            </Typography>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "8px",
              }}
            >
              {userReferralText.referralLink ? (
                <>
                  <InputLabel
                    sx={{
                      fontSize: 11,
                      fontFamily: "Inter",
                      lineHeight: "16px",
                      letterSpacing: "0.4px",
                      color: "#DCDEE4",
                      top: 0,
                    }}
                    htmlFor="email"
                  >
                    Your Referral link:
                  </InputLabel>
                  <StyledAuthInputFields
                    value={userReferralText.referralLink}
                    onClick={handleCopyUserReferralLink}
                    name={"referralLink"}
                    sx={{
                      backgroundColor: "#100f12",
                      width: "100%",
                      input: {
                        color: "rgba(252, 252, 252, 0.6)",
                        cursor: "pointer",
                        caretColor: "transparent",
                      },
                      marginBottom: "5px",
                    }}
                  />
                  <Typography
                    alignItems={"center"}
                    display={"flex"}
                    justifyContent={""}
                    variant="body1"
                    fontSize={10}
                  >
                    Referrals: {userReferralText.activeReferrals} of{" "}
                    {userReferralText.requiredReferrals} {userReferralText.reward === 'pro' ? '' : 'premium subscriptions at $15/month'}
                  </Typography>
                </>
              ) : (
                <>
                  <StyledAuthButton
                    variant={"contained"}
                    sx={{
                      width: "100%",
                      mx: "auto",
                      marginBottom: "0px",
                      marginTop: "0px",
                    }}
                    onClick={handleGenerateUserReferralLink}
                    disabled={isLoadingReferralInfo}
                  >
                    Get My Link
                  </StyledAuthButton>
                </>
              )}
            </Box>
          </StyledAccountContent>
        )}
        <StyledAccountContent>
          <Typography
            alignItems={"center"}
            display={"flex"}
            justifyContent={"space-between"}
            variant="h2"
            color="#F3F3F5"
          >
            Download Density
          </Typography>
          <Box
            display={"flex"}
            width={"100%"}
            justifyContent={"space-between"}
            alignItems={"center"}
          >
            <Typography
              display={"flex"}
              variant="body1"
              fontSize={12}
              alignItems={"center"}
            >
              Version:&nbsp;
              <span style={{ color: "#fcfcfc" }}>
                <DawVersion />
              </span>
            </Typography>
            <a
              href={getDownloadLink(deviceInfo)}
              download
            >
              <StyledAuthButton
                onClick={handleDownload}
                variant={"outlined"}
                sx={{
                  m: 0,
                  width: 148,
                  background: "rgba(255, 255, 255, 0.08)",
                  borderColor: "rgba(252, 252, 252, 0.5)",
                  letterSpacing: "0.5px",
                  fontSize: "11px",
                  color: "rgba(252, 252, 252, 0.6)",
                  fontWeight: 600,
                }}
              >
                Download
              </StyledAuthButton>
            </a>
          </Box>
        </StyledAccountContent>
      </Box>
      <ChangeEmailModal
        open={changeEmailModalOpen}
        handleClose={() => setChangeEmailModalOpen(false)}
        setUser={setUser}
      />
      <ChangePasswordModal
        open={changePasswordModalOpen}
        handleClose={() => setChangePasswordModalOpen(false)}
      />
      <CancelPlanModal
        open={cancelPlanModalOpen}
        handleClose={() => setCancelPlanModalOpen(false)}
        openSuccessModal={() => {
          setCancelPlanModalOpen(false);
          setCancelPlanSuccessModalOpen(true);
          getSubscriptions();
        }}
        lowerMyRate={lowerMyRate}
        planId={subscriptions?.id}
        subscription={subscriptions}
      />
      <CancelPlanSuccessModal
        subscriptions={subscriptions}
        open={cancelPlanSuccessModalOpen}
        handleClose={handleCancelPlanSuccessModalClose}
        renewSubscription={handleKeepSubscription}
        cancelScheduledPause={handleCancelScheduledPauseFromModal}
      />
      <PaymentDetailsChangedModal
        open={isPaymentModalOpen}
        handleClose={handleClosePaymentDetailsModal}
        price={
          subscriptions?.unit_price
            ? `$${subscriptions?.unit_price / 100}`
            : `$0`
        }
      />
      <ConfirmRenewalModal
        open={renewalModalOpen}
        handleClose={() => setRenewalModalOpen(false)}
        handleRenewal={handleSubscriptionRenewal}
        subscriptionPrice={`$${subscriptions?.unit_price / 100}`}
        action={
          typeof subscriptions?.next_billing_at === "undefined"
            ? subscriptions?.status === "non_renewing"
              ? `Reactivate`
              : `Renew`
            : "Cancel"
        }
      />
      <UpgradeModal
        error={""}
        open={upgradeData.open || false}
        url={upgradeData.url || ""}
        handleClose={upgradeData.handleCloseModal || (() => setUpgradeData({ ...upgradeData, open: false }))}
        price={upgradeData.price}
        additionalText={upgradeData.additionalText}
        title={upgradeData.title}
        image={upgradeData.image}
      />
      {/* RTOBuyoutModal removed - component doesn't exist */}
    </StyledDashContainer>
  );
};
